import IconButton from '@material-ui/core/IconButton';
import merge from 'lodash/merge';
import * as React from 'react';
import React__default, { createContext, useCallback, useMemo, useContext } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.min.css';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import { makeStyles } from '@material-ui/core/styles';

var getCss = function getCss(defaultAlertTheme, theme, type, property) {
  var _a, _b, _c, _d;
  return Object.assign(Object.assign(Object.assign({}, defaultAlertTheme[property]), (_a = theme.alerts) === null || _a === void 0 ? void 0 : _a[property]), (_d = (_c = (_b = theme.alerts) === null || _b === void 0 ? void 0 : _b.types) === null || _c === void 0 ? void 0 : _c[type]) === null || _d === void 0 ? void 0 : _d[property]);
};

var textColor = "#242424";
var iconColor = "#1D1D1B";
var successColor = "#E0FCD6";
var darkSuccessColor = "#4FA46D";
var errorColor = "#FEE0D3";
var darkErrorColor = "#BA4242";
var infoColor = "#E2FDFF";
var darkInfoColor = "#1988CC";
var warningColor = "#FFF5D8";
var darkWarningColor = "#CA9801";

var defaultAlertStyle = {
  toast: {
    minHeight: "80px",
    overflow: "visible",
    boxShadow: "none"
  },
  contentWrapper: {
    color: textColor
  },
  title: {
    fontWeight: "bold",
    fontSize: "18px"
  },
  content: {
    fontSize: "14px",
    fontWeight: 500
  },
  icon: {
    padding: "0px 20px 0px 10px",
    flexShrink: 0,
    boxSizing: "content-box"
  },
  contentRoot: {
    display: "flex",
    alignItems: "center",
    padding: "10px 0"
  },
  progress: {
    bottom: "-10px"
  }
};
var defaultBaseAlerts = {
  successRoot: {
    background: successColor
  },
  successProgress: {
    backgroundColor: darkSuccessColor
  },
  successIcon: {
    "& path": {
      fill: darkSuccessColor
    }
  },
  errorRoot: {
    background: errorColor
  },
  errorProgress: {
    backgroundColor: darkErrorColor
  },
  errorIcon: {
    "& path": {
      fill: darkErrorColor
    }
  },
  infoRoot: {
    background: infoColor
  },
  infoProgress: {
    backgroundColor: darkInfoColor
  },
  infoIcon: {
    "& path": {
      fill: darkInfoColor
    }
  },
  warningRoot: {
    background: warningColor
  },
  warningProgress: {
    backgroundColor: darkWarningColor
  },
  warningIcon: {
    "& path": {
      fill: darkWarningColor
    }
  }
};
var getAlertsStyle = function getAlertsStyle(alerts) {
  return makeStyles(function (theme) {
    var _a, _b;
    var styles = {};
    Object.keys(alerts).forEach(function (type) {
      styles["".concat(type, "Root")] = getCss(defaultAlertStyle, theme, type, "toast");
      styles["".concat(type, "Body")] = getCss(defaultAlertStyle, theme, type, "body");
      styles["".concat(type, "Progress")] = getCss(defaultAlertStyle, theme, type, "progress");
      styles["".concat(type, "ContentRoot")] = getCss(defaultAlertStyle, theme, type, "contentRoot");
      styles["".concat(type, "Icon")] = getCss(defaultAlertStyle, theme, type, "icon");
      styles["".concat(type, "Title")] = getCss(defaultAlertStyle, theme, type, "title");
      styles["".concat(type, "Content")] = getCss(defaultAlertStyle, theme, type, "content");
      styles["".concat(type, "ContentWrapper")] = getCss(defaultAlertStyle, theme, type, "contentWrapper");
    });
    return merge({
      root: Object.assign(_defineProperty({
        width: "500px"
      }, theme.breakpoints.down("xs"), {
        width: "95%"
      }), (_a = theme.alerts) === null || _a === void 0 ? void 0 : _a.root),
      closeIcon: Object.assign({
        "& button": {
          padding: "7px"
        },
        "& svg": {
          width: "12px",
          height: "12px",
          "& path": {
            fill: iconColor
          }
        }
      }, (_b = theme.alerts) === null || _b === void 0 ? void 0 : _b.closeIcon)
    }, defaultBaseAlerts, styles);
  });
};

var AlertsContext = createContext({});

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

var BaseAlert = function BaseAlert(_ref) {
  var type = _ref.type,
    classes = _ref.classes,
    Icon = _ref.icon,
    title = _ref.title,
    message = _ref.message;
  var iconComponent;
  if (Icon) {
    iconComponent = React__default.createElement(Icon, {
      className: classes["".concat(type, "Icon")]
    });
  }
  var titleComponent;
  if (title) {
    titleComponent = React__default.createElement("div", {
      className: classes["".concat(type, "Title")]
    }, title);
  }
  return React__default.createElement("div", {
    className: classes["".concat(type, "ContentRoot")]
  }, iconComponent, React__default.createElement("div", {
    className: classes["".concat(type, "ContentWrapper")]
  }, titleComponent, React__default.createElement("div", {
    className: classes["".concat(type, "Content")]
  }, message)));
};

var defaultTypes = ["success", "error", "info", "warning"];
var useAlertsAPI = function useAlertsAPI(alerts, classes) {
  toast.dismiss();
  var closeAlert = useCallback(function (id) {
    toast.dismiss(id);
  }, []);
  var closeAllAlerts = useCallback(function () {
    toast.dismiss();
  }, []);
  var methods = useMemo(function () {
    var result = {};
    Object.keys(alerts).forEach(function (index) {
      var _a = alerts[index],
        title = _a.title,
        icon = _a.icon,
        toastOptions = __rest(_a, ["title", "icon"]);
      result[index] = function (content) {
        var customOptions = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
        var optionClasses = {
          className: classes["".concat(index, "Root")],
          bodyClassName: classes["".concat(index, "Body")],
          progressClassName: classes["".concat(index, "Progress")]
        };
        var options = merge({}, toastOptions, optionClasses, customOptions);
        if (isBaseAlertType(index)) {
          return toast[index](React__default.createElement(BaseAlert, {
            type: index,
            title: options.title || title,
            icon: icon,
            message: content,
            classes: classes
          }), options);
        }
        return toast(content, options);
      };
    });
    return result;
  }, [alerts, classes]);
  return Object.assign({
    closeAlert: closeAlert,
    closeAllAlerts: closeAllAlerts
  }, methods);
};
var isBaseAlertType = function isBaseAlertType(type) {
  return defaultTypes.includes(type);
};

var CloseIcon = function CloseIcon(props) {
  return React.createElement("svg", Object.assign({
    width: "1em",
    height: "1em",
    viewBox: "0 0 20.356 20.357"
  }, props), React.createElement("path", {
    d: "M1.047 20.357a1.048 1.048 0 01-.74-1.788L18.569.307a1.047 1.047 0 011.481 1.481L1.788 20.05a1.05 1.05 0 01-.741.307zm0 0"
  }), React.createElement("path", {
    d: "M19.31 20.357a1.039 1.039 0 01-.74-.307L.307 1.788A1.047 1.047 0 011.788.307L20.05 18.569a1.048 1.048 0 01-.74 1.788zm0 0"
  }));
};

function ErrorIcon(props) {
  return React.createElement("svg", Object.assign({
    xmlns: "http://www.w3.org/2000/svg",
    width: "36.988",
    height: "36.988",
    viewBox: "0 0 36.988 36.988"
  }, props), React.createElement("g", {
    "data-name": "Grupo 26206"
  }, React.createElement("g", {
    "data-name": "Grupo 26202",
    fill: "#040405"
  }, React.createElement("path", {
    "data-name": "Caminho 84579",
    d: "M5.414 5.42a18.494 18.494 0 1026.154-.006 18.494 18.494 0 00-26.154.006zm29.4 13.076a16.351 16.351 0 01-16.331 16.331 16.332 16.332 0 1116.331-16.333z"
  }), React.createElement("path", {
    "data-name": "Caminho 84580",
    d: "M25.087 23.507l-4.795-4.8 4.8-4.8a1.124 1.124 0 00-1.589-1.584l-4.8 4.8-4.8-4.8a1.124 1.124 0 00-1.717 1.451 1.2 1.2 0 00.133.133l4.8 4.8-4.8 4.8a1.124 1.124 0 001.584 1.584l4.8-4.8 4.8 4.8a1.124 1.124 0 001.717-1.451 1.043 1.043 0 00-.133-.133z"
  }))));
}

function InfoIcon(props) {
  return React.createElement("svg", Object.assign({
    xmlns: "http://www.w3.org/2000/svg",
    width: "36.988",
    height: "36.988",
    viewBox: "0 0 36.988 36.988"
  }, props), React.createElement("g", {
    "data-name": "Grupo 26203",
    fill: "#040405"
  }, React.createElement("path", {
    "data-name": "Caminho 84581",
    d: "M5.414 5.42a18.494 18.494 0 1026.154-.006 18.494 18.494 0 00-26.154.006zm29.4 13.076a16.351 16.351 0 01-16.331 16.331 16.332 16.332 0 1116.331-16.333z"
  }), React.createElement("path", {
    "data-name": "Ret\\xE2ngulo 9395",
    d: "M16.878 15.02h3.121v11.775h-3.121z"
  }), React.createElement("path", {
    "data-name": "Caminho 84582",
    d: "M18.398 10.361a1.684 1.684 0 00-1.634 1.733v.037a1.636 1.636 0 001.674 1.6h.059a1.684 1.684 0 00-.1-3.366z"
  })));
}

function SuccessIcon(props) {
  return React.createElement("svg", Object.assign({
    xmlns: "http://www.w3.org/2000/svg",
    width: "36.988",
    height: "36.988",
    viewBox: "0 0 36.988 36.988"
  }, props), React.createElement("g", {
    "data-name": "Grupo 26205"
  }, React.createElement("g", {
    "data-name": "Grupo 26201",
    fill: "#040405"
  }, React.createElement("path", {
    "data-name": "Caminho 84577",
    d: "M31.568 5.414a18.494 18.494 0 105.42 13.082 18.493 18.493 0 00-5.42-13.082zm-13.08 29.413a16.332 16.332 0 1116.331-16.333 16.351 16.351 0 01-16.331 16.333z"
  }), React.createElement("path", {
    "data-name": "Caminho 84578",
    d: "M25.793 12.561l-9.572 9.57-5.039-5.039a1.084 1.084 0 10-1.535 1.53l5.806 5.806a1.086 1.086 0 001.531 0l10.34-10.34a1.083 1.083 0 10-1.533-1.531z"
  }))));
}

function WarningIcon(props) {
  return React.createElement("svg", Object.assign({
    xmlns: "http://www.w3.org/2000/svg",
    width: "37.001",
    height: "33.257",
    viewBox: "0 0 37.001 33.257"
  }, props), React.createElement("g", {
    "data-name": "Grupo 26204",
    fill: "#040405"
  }, React.createElement("path", {
    "data-name": "Caminho 84583",
    d: "M36.409 26.493l-14-24.241a4.513 4.513 0 00-7.811 0l-14 24.241a4.518 4.518 0 003.907 6.764h27.992a4.516 4.516 0 003.9-6.764zm-2.211 3.229l-.107.181a1.932 1.932 0 01-1.57.8h.005H4.512a1.953 1.953 0 01-1.691-2.932l13.993-24.24a1.985 1.985 0 011.7-.98 1.956 1.956 0 01.973.263 1.99 1.99 0 01.718.717l13.993 24.24a1.945 1.945 0 010 1.951z"
  }), React.createElement("path", {
    "data-name": "Ret\\xE2ngulo 9396",
    d: "M17.245 10.774h2.523v10.989h-2.523z"
  }), React.createElement("path", {
    "data-name": "Caminho 84584",
    d: "M18.51 23.609a1.621 1.621 0 101.621 1.621 1.621 1.621 0 00-1.621-1.621z"
  })));
}

var Alerts = function Alerts(_ref) {
  var customConfig = _ref.config,
    customAlerts = _ref.customAlerts,
    apiRef = _ref.apiRef,
    children = _ref.children;
  var alerts = useMemo(function () {
    var defaultAlerts = {
      success: {
        title: "Sucesso!",
        icon: SuccessIcon
      },
      error: {
        title: "Erro!",
        icon: ErrorIcon
      },
      info: {
        title: "Informação!",
        icon: InfoIcon
      },
      warning: {
        title: "Aviso!",
        icon: WarningIcon
      }
    };
    return merge({}, defaultAlerts, customAlerts);
  }, [customAlerts]);
  var useStyles = useMemo(function () {
    return getAlertsStyle(alerts);
  }, [alerts]);
  var classes = useStyles({});
  var CloseButton = useCallback(function (_ref2) {
    var closeToast = _ref2.closeToast;
    return React__default.createElement("div", {
      className: classes.closeIcon
    }, React__default.createElement(IconButton, {
      onClick: closeToast
    }, React__default.createElement(CloseIcon, null)));
  }, [classes]);
  var config = useMemo(function () {
    var defaultConfiguration = {
      closeButton: CloseButton,
      className: classes.root,
      closeOnClick: false,
      limit: 5
    };
    return merge(defaultConfiguration, customConfig);
  }, [customConfig, classes, CloseButton]);
  var api = useAlertsAPI(alerts, classes);
  if (apiRef) {
    // eslint-disable-next-line no-param-reassign
    apiRef.current = api;
  }
  return React__default.createElement(AlertsContext.Provider, {
    value: api
  }, React__default.createElement(ToastContainer, Object.assign({}, config)), children);
};

var useAlerts = function useAlerts() {
  return useContext(AlertsContext);
};

export { Alerts, useAlerts };
//# sourceMappingURL=main.esm.js.map
